import { useState } from 'react';
import { Record } from '@phosphor-icons/react';
import { Button, Dialog, DialogContent, DialogFooter, DialogTrigger, Text } from '@chatsdet/ui';
import type { WorkflowSource } from './WorkflowSettingsOverlay';

interface RecordingSourceSettingsProps {
  source: Pick<WorkflowSource, 'externalKey'>;
  isRegenerating: boolean;
  onRegenerate: (guidance: string) => Promise<boolean>;
}

export function RecordingSourceSettings({
  source,
  isRegenerating,
  onRegenerate,
}: RecordingSourceSettingsProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [guidance, setGuidance] = useState('');

  const cancelRegenerate = () => {
    setIsConfirmOpen(false);
    setGuidance('');
  };

  const confirmRegenerate = async () => {
    const succeeded = await onRegenerate(guidance.trim());
    if (!succeeded) return;
    setIsConfirmOpen(false);
    setGuidance('');
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none">Source</label>
      <div className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5">
        <Record size={18} className="shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <Text size="sm">Recording from Chrome extension</Text>
          {source.externalKey && (
            <Text size="xs" className="text-muted-foreground font-mono truncate">
              {source.externalKey}
            </Text>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onPress={() => setIsConfirmOpen(true)}
          isDisabled={isRegenerating}
        >
          {isRegenerating ? 'Regenerating…' : 'Regenerate'}
        </Button>
      </div>
      <Text size="xs" className="text-muted-foreground">
        Wipes the current nodes and re-runs the recording author on the same trace.
      </Text>
      <DialogTrigger isOpen={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <span />
        <Dialog title="Regenerate from recording?">
          <DialogContent>
            <div className="space-y-3">
              <Text size="sm">
                This will wipe the current nodes, edges, and cache and re-run the recording author
                on the original trace using the same title and intent. Add optional guidance below
                to steer this attempt.
              </Text>
              <div className="space-y-1.5">
                <label htmlFor="regenerate-guidance" className="text-sm font-medium leading-none">
                  Additional guidance (optional)
                </label>
                <textarea
                  id="regenerate-guidance"
                  value={guidance}
                  onChange={(event) => setGuidance(event.target.value)}
                  rows={3}
                  className="flex w-full rounded-md border border-border bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y min-h-[72px]"
                  placeholder="e.g. The last attempt skipped the confirmation modal — make sure the flow waits for and dismisses it."
                  disabled={isRegenerating}
                />
                <Text size="xs" className="text-muted-foreground">
                  Folded into the flow&apos;s intent to steer this regeneration — e.g. what the last
                  attempt got wrong.
                </Text>
              </div>
            </div>
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={cancelRegenerate} isDisabled={isRegenerating}>
              Cancel
            </Button>
            <Button variant="primary" onPress={confirmRegenerate} isDisabled={isRegenerating}>
              {isRegenerating ? 'Regenerating…' : 'Regenerate'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </div>
  );
}
